import type { CheckoutApi } from '@/features/billing/api/billing-api'
import { Id } from '@/infra/id'
import type { CheckoutLink } from './CheckoutLink'
import { mapCheckoutLinkResourceToCheckoutLink } from './checkout-link-mappers'

/**
 * Parameters for emailing a job's checkout link.
 */
export interface EmailCheckoutLinkParams {
  workspaceId: Id
  userId: Id
  jobId: Id
}

/**
 * External Checkout Store.
 */
export class ExternalCheckoutStore {
  constructor(private readonly _api: CheckoutApi) {}

  /**
   * Generates a Stripe checkout link for a job.
   */
  async generateCheckoutLink(jobId: Id): Promise<CheckoutLink> {
    const resource = await this._api.generateCheckoutLink(Id.assert(jobId))
    return mapCheckoutLinkResourceToCheckoutLink(resource)
  }

  /**
   * Invalidates a job's open checkout sessions.
   */
  async invalidateCheckoutSessions(jobId: Id, force: boolean) {
    return this._api.invalidateCheckoutSessions(Id.assert(jobId), force)
  }

  /**
   * Emails a job's checkout link.
   */
  async emailCheckoutLink({
    workspaceId,
    userId,
    jobId,
  }: EmailCheckoutLinkParams) {
    return this._api.emailCheckoutLink({
      workspace_id: Id.assert(workspaceId),
      user_id: Id.assert(userId),
      job_id: Id.assert(jobId),
    })
  }
}
